/**
 * /waitlist-remove?token=… — one-click self-service removal from the waitlist
 * (Phase 118-06, compliance gap G15).
 *
 * PUBLIC. Reached from the link in every waitlist email by a lead who may have
 * no account, so it needs no session and is not behind any auth redirect (the
 * root Stack guards nothing; like /privacy and /claim/[token] this route simply
 * never asks who you are).
 *
 * NOTHING HAPPENS ON LOAD. Mail scanners and link previewers fetch every link
 * in an email; if opening the page erased the lead, a corporate spam filter
 * would remove people who never clicked. The erasure runs only when the person
 * presses the button.
 *
 * NO ORACLE. The page never shows the address and never asks the server about
 * the link before the press. The server answers an unknown, expired, reused or
 * replaced link identically, so the page can only say "this link no longer
 * works" — it cannot tell anyone whether an address is on the list.
 *
 * FRAME. `PublicLayout`, the frame every public page uses (privacy, terms,
 * claim): the landing-page nav and footer on web at every width — the nav bar
 * collapses to a menu below 768px — and a header with a safe Back on native.
 * `ScreenFrame` is the in-app frame: above 768px it hands page identity to the
 * signed-in sidebar, which a signed-out visitor does not have, so the page
 * would have no title on desktop.
 */

import { useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation } from '@tanstack/react-query';
import Animated, { FadeIn, FadeInDown, ZoomIn } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { LinearGradient } from 'expo-linear-gradient';
import { AlertTriangle, Check, Clock, Link2Off, Trash2 } from 'lucide-react-native';

import AnimatedPressable from '@/components/AnimatedPressable';
import { PublicLayout } from '@/components/PublicLayout';
import { isWeb } from '@/lib/platform';
import { openExternalUrl } from '@/lib/linking';
import { handleError } from '@/lib/errors';
import {
  readRemovalToken,
  removeFromWaitlist,
  type WaitlistRemovalOutcome,
} from '@/lib/waitlist/removal';

/** The card carries its own surface so it reads the same on the dark web
 *  landing frame and on the light native frame. Same tokens as PublicLayout. */
const P = {
  card: '#0a1628',
  cardEdge: 'rgba(79,209,197,0.22)',
  glow: 'rgba(79,209,197,0.16)',
  teal: '#4fd1c5',
  tealDeep: '#0f766e',
  white: '#FFFFFF',
  text1: 'rgba(255,255,255,0.88)',
  text2: 'rgba(255,255,255,0.62)',
  text3: 'rgba(255,255,255,0.42)',
  rule: 'rgba(255,255,255,0.08)',
  danger: '#f87171',
  dangerBg: 'rgba(248,113,113,0.12)',
};

const PRIVACY_MAILTO = 'mailto:privacy@inverio.ai?subject=Remove%20me%20from%20the%20waitlist';

const WHAT_GOES = [
  'Your name, email, company and title',
  'Your LinkedIn link and role',
  'Your place in line and your referral code',
];

export default function WaitlistRemoveScreen() {
  const params = useLocalSearchParams<{ token?: string | string[] }>();
  const token = readRemovalToken(params.token);
  const [failure, setFailure] = useState<string | null>(null);

  const removal = useMutation<WaitlistRemovalOutcome, Error, string>({
    mutationFn: (t) => removeFromWaitlist(t),
    onMutate: () => setFailure(null),
    onSuccess: (outcome) => {
      if (!isWeb) {
        Haptics.notificationAsync(
          outcome === 'removed'
            ? Haptics.NotificationFeedbackType.Success
            : Haptics.NotificationFeedbackType.Warning,
        );
      }
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, { context: 'WaitlistRemoveScreen.remove' });
      if (!isCancelled) setFailure(message);
    },
  });

  const outcome = removal.data;

  return (
    <PublicLayout title="Leave the waitlist" maxWidth={560}>
      {/* Registered here rather than in the root _layout: a public page owns
          its own chrome via PublicLayout, so the navigator draws none. */}
      <Stack.Screen options={{ headerShown: false, title: 'Leave the waitlist' }} />
      <View style={{ paddingTop: isWeb ? 8 : 0, paddingBottom: 24 }}>
        <Card>
          {!token ? (
            <Notice
              icon={<Link2Off size={26} color={P.teal} />}
              title="This link is incomplete"
              body="Open the removal link from your most recent Inverio waitlist email, or write to us and we will remove you."
            />
          ) : outcome === 'removed' ? (
            <Removed />
          ) : outcome === 'link_invalid' ? (
            <Notice
              icon={<Link2Off size={26} color={P.teal} />}
              title="This link no longer works"
              body="It has expired, was already used, or a newer email replaced it. Use the link in your most recent waitlist email, or write to us and we will remove you."
            />
          ) : outcome === 'rate_limited' ? (
            <Notice
              icon={<Clock size={26} color={P.teal} />}
              title="Too many attempts"
              body="Please try again in an hour, or write to us and we will remove you."
            />
          ) : (
            <Confirm
              pending={removal.isPending}
              failure={failure}
              onConfirm={() => {
                if (!isWeb) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
                removal.mutate(token);
              }}
            />
          )}
        </Card>

        {outcome !== 'removed' && (
          <Animated.View entering={FadeIn.delay(250).duration(400)} className="mt-5 items-center">
            <Pressable
              onPress={() => openExternalUrl(PRIVACY_MAILTO, 'WaitlistRemoveScreen.mailto')}
              accessibilityRole="link"
              hitSlop={10}
              style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}
            >
              <Text numberOfLines={2} className="text-center text-sm" style={{ color: isWeb ? P.text2 : '#64748B' }}>
                Prefer email? <Text style={{ textDecorationLine: 'underline' }}>privacy@inverio.ai</Text>
              </Text>
            </Pressable>
          </Animated.View>
        )}
      </View>
    </PublicLayout>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Animated.View
      entering={FadeInDown.duration(450)}
      style={{
        borderRadius: 24,
        overflow: 'hidden',
        backgroundColor: P.card,
        borderWidth: 1,
        borderColor: P.cardEdge,
        shadowColor: '#000',
        shadowOpacity: 0.28,
        shadowRadius: 30,
        shadowOffset: { width: 0, height: 16 },
        elevation: 8,
      }}
    >
      {/* A soft teal light from the top edge: depth without a flat slab. */}
      <LinearGradient
        colors={[P.glow, 'rgba(79,209,197,0)']}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 180 }}
        pointerEvents="none"
      />
      <View style={{ padding: isWeb ? 32 : 22 }}>{children}</View>
    </Animated.View>
  );
}

function IconBadge({ children, tone = 'teal' }: { children: React.ReactNode; tone?: 'teal' | 'danger' }) {
  return (
    <View
      style={{
        width: 52,
        height: 52,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tone === 'danger' ? P.dangerBg : 'rgba(79,209,197,0.12)',
        borderWidth: 1,
        borderColor: tone === 'danger' ? 'rgba(248,113,113,0.28)' : P.cardEdge,
      }}
    >
      {children}
    </View>
  );
}

function Confirm({
  pending,
  failure,
  onConfirm,
}: {
  pending: boolean;
  failure: string | null;
  onConfirm: () => void;
}) {
  return (
    <View>
      <IconBadge tone="danger">
        <Trash2 size={24} color={P.danger} />
      </IconBadge>

      <Text numberOfLines={1} className="mt-5 text-xs font-semibold uppercase" style={{ color: P.teal, letterSpacing: 1.6 }}>
        Inverio waitlist
      </Text>
      <Text
        numberOfLines={2}
        accessibilityRole="header"
        className="mt-1 font-bold"
        style={{ color: P.white, fontSize: isWeb ? 30 : 26, lineHeight: isWeb ? 36 : 32, letterSpacing: -0.5 }}
      >
        Leave the waitlist?
      </Text>
      <Text numberOfLines={4} className="mt-3 text-base" style={{ color: P.text2, lineHeight: 23 }}>
        One tap removes you completely. We delete your details straight away and will not email you about the waitlist again.
      </Text>

      <View className="mt-6" style={{ borderTopWidth: 1, borderTopColor: P.rule, paddingTop: 18 }}>
        <Text numberOfLines={1} className="text-xs font-semibold uppercase" style={{ color: P.text3, letterSpacing: 1.2 }}>
          What we delete
        </Text>
        {WHAT_GOES.map((line, i) => (
          <View key={`goes-${i}`} className="mt-3 flex-row items-center">
            <View className="shrink-0" style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: P.teal, marginRight: 12 }} />
            <Text numberOfLines={2} className="flex-1 text-sm" style={{ color: P.text1, lineHeight: 20 }}>
              {line}
            </Text>
          </View>
        ))}
      </View>

      {failure && (
        <Animated.View
          entering={FadeIn.duration(250)}
          className="mt-6 flex-row items-center rounded-2xl"
          style={{ backgroundColor: P.dangerBg, padding: 14 }}
        >
          <View className="shrink-0" style={{ marginRight: 10 }}>
            <AlertTriangle size={18} color={P.danger} />
          </View>
          <Text numberOfLines={3} className="flex-1 text-sm" style={{ color: P.text1, lineHeight: 20 }}>
            {failure}
          </Text>
        </Animated.View>
      )}

      {/* The View owns the dimensions and the Pressable fills it — nothing
          content-sized in between (CLAUDE.md pressable_sizing). */}
      <View
        className="mt-7"
        style={{ height: 54, borderRadius: 16, overflow: 'hidden', backgroundColor: pending ? P.tealDeep : P.teal }}
      >
        <AnimatedPressable
          onPress={onConfirm}
          disabled={pending}
          accessibilityRole="button"
          accessibilityLabel="Remove me from the waitlist"
          accessibilityState={{ disabled: pending, busy: pending }}
          style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}
        >
          {pending ? (
            <>
              <ActivityIndicator color={P.white} />
              <Text numberOfLines={1} className="ml-3 text-base font-semibold" style={{ color: P.white }}>
                Removing you…
              </Text>
            </>
          ) : (
            <Text numberOfLines={1} className="text-base font-bold" style={{ color: '#062421' }}>
              Remove me from the waitlist
            </Text>
          )}
        </AnimatedPressable>
      </View>

      <Text numberOfLines={2} className="mt-4 text-center text-xs" style={{ color: P.text3, lineHeight: 17 }}>
        This cannot be undone. You can always join again later.
      </Text>
    </View>
  );
}

function Removed() {
  const router = useRouter();
  return (
    <View className="items-center" style={{ paddingVertical: 8 }}>
      <Animated.View
        entering={ZoomIn.springify().damping(12)}
        style={{
          width: 72,
          height: 72,
          borderRadius: 36,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: P.teal,
          shadowColor: P.teal,
          shadowOpacity: 0.5,
          shadowRadius: 24,
          shadowOffset: { width: 0, height: 0 },
        }}
      >
        <Check size={36} color="#062421" strokeWidth={3} />
      </Animated.View>
      <Animated.View entering={FadeInDown.delay(150).duration(400)} className="items-center">
        <Text
          numberOfLines={2}
          accessibilityRole="header"
          className="mt-6 text-center font-bold"
          style={{ color: P.white, fontSize: 26, lineHeight: 32, letterSpacing: -0.4 }}
        >
          You have been removed
        </Text>
        <Text numberOfLines={4} className="mt-3 text-center text-base" style={{ color: P.text2, lineHeight: 23 }}>
          Your waitlist details are deleted. You will not hear from us about the waitlist again.
        </Text>
      </Animated.View>
      <View className="mt-7 self-stretch" style={{ height: 50, borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: P.cardEdge }}>
        <AnimatedPressable
          onPress={() => router.replace('/')}
          accessibilityRole="button"
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          <Text numberOfLines={1} className="text-base font-semibold" style={{ color: P.teal }}>
            Back to Inverio
          </Text>
        </AnimatedPressable>
      </View>
    </View>
  );
}

function Notice({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <View>
      <IconBadge>{icon}</IconBadge>
      <Text
        numberOfLines={2}
        accessibilityRole="header"
        className="mt-5 font-bold"
        style={{ color: P.white, fontSize: 24, lineHeight: 30, letterSpacing: -0.4 }}
      >
        {title}
      </Text>
      <Text numberOfLines={5} className="mt-3 text-base" style={{ color: P.text2, lineHeight: 23 }}>
        {body}
      </Text>
    </View>
  );
}
